/**
 * InfraLayoutEngine
 *
 * Deterministic infrastructure-aware layout for Compass network views.
 *
 * Why not ELK? ELK needs a real Web Worker (blob URL) or the setTimeout
 * shim — neither runs reliably in the Cloudflare Workers runtime that
 * hosts CompassViewMaterializer. And even when it runs, ELK's layered
 * DAG layout doesn't understand infrastructure semantics: it treats an
 * ALB fanned out to 12 EC2s the same as any other hub-and-spoke.
 *
 * This engine produces a layout tailored to how people read cloud
 * architecture diagrams:
 *
 *   ┌──────────── VPC ────────────────────────────────────┐
 *   │ [Ingress row: ALB, IGW, CloudFront]                 │
 *   │ ┌─ public-a ─────────┐ ┌─ public-b ───────┐        │
 *   │ │ [NAT] [EC2 ×3]     │ │ [NAT] [EC2 ×3]   │        │
 *   │ └────────────────────┘ └──────────────────┘        │
 *   │ ┌─ private-a ────────┐ ┌─ private-b ──────┐        │
 *   │ │ [EC2 ×5] [Lambda×2]│ │ [EC2 ×5]         │        │
 *   │ └────────────────────┘ └──────────────────┘        │
 *   │ ┌─ data-a ───────────┐ ┌─ data-b ─────────┐        │
 *   │ │ [RDS] [Cache]      │ │ [RDS]            │        │
 *   │ └────────────────────┘ └──────────────────┘        │
 *   └─────────────────────────────────────────────────────┘
 *   ┌── Shared ─────────────────────────────────────────────┐
 *   │ [IAM ×12] [S3 ×4] [KMS ×6] [Route53] [Secrets ×3]    │
 *   └───────────────────────────────────────────────────────┘
 *
 * Key behaviors:
 *
 *  • Containment → frames. VPCs and subnets become frames sized from
 *    their contents. ELK-style "hierarchy" is unnecessary.
 *
 *  • Duplicate clustering. N identical resources in the same subnet
 *    collapse to a single shape with a count badge. Edges to absorbed
 *    members rewire to the cluster representative.
 *
 *  • Tier-aware ordering. Leaves sort into rows by role:
 *      ingress → network → compute → data → other
 *    Subnets sort into VPC rows by role:
 *      public  → private → data
 *
 *  • Shared-services lane. Cross-cutting resources (IAM, S3, KMS,
 *    Route53, etc.) with no VPC parent are pulled into a bottom band,
 *    aggressively clustered.
 *
 *  • Fan-in-aware positioning. Leaves with ≥ FAN_IN_THRESHOLD incoming
 *    edges from different sources are hoisted to the VPC ingress row
 *    (e.g. a shared ALB targeted by many EC2 groups).
 *
 * Pure math, no external layout libs — runs in any JS runtime.
 */

export type InfraTier = 'ingress' | 'network' | 'compute' | 'data' | 'shared' | 'other';
export type SubnetTier = 'public' | 'private' | 'data';

/**
 * Edge tier. Drives visual weight on the canvas:
 *   - data:       bold, always visible (traffic + data associations)
 *   - control:    faint, visible on hover/select (IAM/KMS/logs)
 *   - default:    hidden (default VPC/SG memberships)
 *   - structural: rendered as frame containment, not a connector
 */
export type EdgeTier = 'data' | 'control' | 'default' | 'structural';

export interface InfraLayoutLeaf {
  id: string;
  manifestId: string;    // e.g. "aws_ec2", "aws_alb"
  resourceType: string;  // short form, e.g. "ec2"
  name: string;
  width: number;
  height: number;
  /**
   * Resource tags. Used for role-aware clustering: `tags.role`, `tags.Role`,
   * `tags.service`, and AWS-managed tags for ASG/CFN membership let us
   * group "app-servers ×6" rather than "ec2 ×6".
   */
  tags?: Record<string, string>;
  /** AZ code, e.g. "us-east-1a". Drives AZ columns in M2. */
  az?: string;
}

export interface InfraLayoutContainer {
  id: string;
  manifestId: string;    // "aws_vpc", "aws_subnet", ...
  resourceType: string;  // "vpc", "subnet", "region", "account"
  name: string;
}

export interface InfraLayoutEdge {
  id: string;
  source: string;
  target: string;
  kind: string;
}

export interface InfraLayoutInput {
  leaves: InfraLayoutLeaf[];
  containers: InfraLayoutContainer[];
  containmentEdges: InfraLayoutEdge[]; // source = parent, target = child
  flowEdges: InfraLayoutEdge[];        // all other edges
}

export interface InfraCluster {
  id: string;                 // synthetic id: "cluster:<parentId>:<manifestId>:<roleKey>"
  repId: string;              // first member's leaf id — becomes the shape id
  manifestId: string;
  resourceType: string;
  memberIds: string[];
  count: number;
  parentContainerId: string | null;
  tier: InfraTier;
  width: number;
  height: number;
  label: string;              // e.g. "app-servers ×6" or "ec2 ×12"
  /** Tag-derived role label, null when falling back to manifest-only grouping. */
  roleLabel: string | null;
  /** Normalised key used for grouping; same value across all members. */
  roleKey: string | null;
}

export interface InfraFrame {
  x: number;
  y: number;
  width: number;
  height: number;
  containerId: string;
  resourceType: string;
  tier?: SubnetTier;
  /**
   * True when this subnet's stack signature matches another same-tier
   * subnet in the same VPC. Canvas uses this to render mirrored
   * subnets side-by-side with a shared header.
   */
  mirrored?: boolean;
  /** Signature string used for mirror detection; opaque to callers. */
  signature?: string;
}

/**
 * A shared resource (IAM, KMS, S3, etc.) plus the list of resources
 * that reference it. Emitted on {@link InfraLayoutOutput.sharedReferences}
 * for the M2 chips + side-drawer UX.
 */
export interface SharedReference {
  resourceId: string;
  manifestId: string;
  resourceType: string;
  label: string;
  referencedBy: string[];
}

export interface InfraLayoutOutput {
  /** Absolute position for each visible node (leaf rep or cluster rep). */
  positions: Map<string, { x: number; y: number }>;
  /** Which leaves became clusters. Rendered as a single stacked shape. */
  clusters: Map<string, InfraCluster>;
  /** Leaf ids that were absorbed into clusters and should NOT render. */
  hiddenMemberIds: Set<string>;
  /** memberId → clusterRepId. Use to rewire edge endpoints. */
  edgeRewrites: Map<string, string>;
  /** Frames for containers (VPC, subnet, …). */
  frames: Map<string, InfraFrame>;
  /** Shape children of each container (rep ids only). */
  frameShapes: Map<string, string[]>;
  /**
   * Shared resources referenced by in-view resources. Not positioned —
   * rendered by the canvas as chips/drawer. Indexed by shared resource id.
   */
  sharedReferences: Map<string, SharedReference>;
  /** Total content bounds, useful for positioning aggregates below. */
  totalBounds: { width: number; height: number };
}

// ─── Classification ──────────────────────────────────────────────

const INGRESS_TYPES = new Set([
  'aws_alb', 'aws_nlb', 'aws_elb', 'aws_clb',
  'aws_igw', 'aws_internet_gateway',
  'aws_cloudfront', 'aws_cloudfront_distribution',
  'aws_api_gateway', 'aws_apigateway', 'aws_apigatewayv2',
  'aws_waf', 'aws_shield',
  'aws_global_accelerator',
]);

const NETWORK_TYPES = new Set([
  'aws_nat', 'aws_nat_gateway',
  'aws_vpc_endpoint', 'aws_vpce',
  'aws_tgw', 'aws_transit_gateway',
  'aws_vpn', 'aws_vpn_gateway',
  'aws_direct_connect',
  'aws_route_table', 'aws_network_acl',
  'aws_peering',
]);

const COMPUTE_TYPES = new Set([
  'aws_ec2', 'aws_instance',
  'aws_asg', 'aws_auto_scaling_group',
  'aws_lambda', 'aws_lambda_function',
  'aws_ecs_service', 'aws_ecs_task', 'aws_ecs_cluster',
  'aws_fargate',
  'aws_batch',
  'aws_lightsail',
  'aws_beanstalk',
  'aws_eks', 'aws_eks_cluster', 'aws_eks_nodegroup',
  'aws_ec2_instance',
]);

const DATA_TYPES = new Set([
  'aws_rds', 'aws_rds_instance', 'aws_rds_cluster',
  'aws_aurora',
  'aws_dynamodb', 'aws_dynamodb_table',
  'aws_elasticache', 'aws_elasticache_cluster',
  'aws_redshift',
  'aws_documentdb',
  'aws_neptune',
  'aws_memorydb',
  'aws_timestream',
  'aws_keyspaces',
]);

const SHARED_TYPES = new Set([
  'aws_s3', 'aws_s3_bucket',
  'aws_iam', 'aws_iam_role', 'aws_iam_policy', 'aws_iam_user', 'aws_iam_group',
  'aws_kms', 'aws_kms_key',
  'aws_secrets_manager', 'aws_secret',
  'aws_route53', 'aws_route53_zone', 'aws_route53_record',
  'aws_cloudwatch', 'aws_cloudwatch_alarm', 'aws_cloudwatch_log_group',
  'aws_sns', 'aws_sqs',
  'aws_step_functions',
  'aws_eventbridge', 'aws_cloudtrail',
  'aws_ssm', 'aws_ssm_parameter',
  'aws_acm', 'aws_acm_certificate',
  'aws_ses', 'aws_sesv2',
  'aws_glue', 'aws_athena',
]);

function classifyTier(manifestId: string): InfraTier {
  if (INGRESS_TYPES.has(manifestId)) return 'ingress';
  if (NETWORK_TYPES.has(manifestId)) return 'network';
  if (COMPUTE_TYPES.has(manifestId)) return 'compute';
  if (DATA_TYPES.has(manifestId)) return 'data';
  if (SHARED_TYPES.has(manifestId)) return 'shared';
  return 'other';
}

/** Public version of classifyTier for use outside the engine. */
export function classifyInfraTier(manifestId: string): InfraTier {
  return classifyTier(manifestId);
}

/**
 * Classify an edge into a visual tier based on its kind + the semantic
 * tier of its endpoints. Pure function — safe to call from anywhere.
 *
 * Rules (most specific first):
 *   1. Containment edges are structural (rendered as frame boundaries).
 *   2. IAM / KMS / encryption / identity edges → control plane.
 *   3. Edges targeting a `shared`-tier resource (IAM/KMS/S3/SNS) →
 *      control plane (most shared-resource references are admin, not
 *      data-plane traffic).
 *   4. Anything else explicitly traffic-shaped (connects, data_flow,
 *      sg_access) → data plane.
 *   5. Fallback → data plane. Prefer showing edges over hiding them;
 *      the user can filter to "data only" if noise is a problem.
 */
export function classifyEdgeTier(
  edge: { kind: string },
  sourceTier: InfraTier | undefined,
  targetTier: InfraTier | undefined,
): EdgeTier {
  const k = edge.kind;
  // Structural = anything that *is* parent→child containment. Must stay in
  // sync with `CONTAINMENT_EDGE_TYPES` in focusFilter.ts — both live-wire
  // sets together.
  if (
    k === 'contains' ||
    k === 'containment' ||
    k === 'in-vpc' ||
    k === 'in-subnet' ||
    k === 'parent-of'
  ) {
    return 'structural';
  }
  if (k === 'assumes' || k === 'encrypts' || k === 'identity') return 'control';
  if (targetTier === 'shared' || sourceTier === 'shared') return 'control';
  if (k === 'connects' || k === 'data_flow' || k === 'sg_access') return 'data';
  return 'data';
}

/**
 * Derive a role key from resource tags. Priority order:
 *
 *   1. tags.role / tags.Role
 *   2. tags.service / tags.Service
 *   3. aws:autoscaling:groupName (ASG membership)
 *   4. aws:cloudformation:stack-name (CFN stack membership)
 *   5. tags.Component
 *   6. tags.app / tags.App / tags.application / tags.Application
 *
 * Returns null if no candidate tag is present, which triggers the
 * manifest-only fallback path.
 */
function roleKeyOf(leaf: InfraLayoutLeaf): string | null {
  const tags = leaf.tags;
  if (!tags) return null;
  const candidates = [
    tags.role, tags.Role,
    tags.service, tags.Service,
    tags['aws:autoscaling:groupName'],
    tags['aws:cloudformation:stack-name'],
    tags.Component, tags.component,
    tags.app, tags.App, tags.application, tags.Application,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) {
      return normaliseRoleKey(c);
    }
  }
  return null;
}

function normaliseRoleKey(raw: string): string {
  return raw.toLowerCase().trim().replace(/\s+/g, '-');
}

/**
 * Turn a role-key back into a label fit for display.
 * `web-app` → `web-app`; `WebApp` (already normalised to `webapp`) stays.
 * Length caps at 28 chars with ellipsis to keep shape labels tidy.
 */
function humanizeRoleKey(roleKey: string): string {
  const MAX = 28;
  return roleKey.length > MAX ? roleKey.slice(0, MAX - 1) + '…' : roleKey;
}

/**
 * Deterministic stack signature for a subnet or AZ column — a sorted,
 * role-aware tally of its leaves. Two buckets with the same signature
 * are mirror copies (same resource types + same roles + same counts).
 *
 * Format: "manifest:roleKey:count|manifest:roleKey:count|..."
 */
function stackSignature(
  leafIds: string[],
  leafById: Map<string, InfraLayoutLeaf>,
): string {
  const tally = new Map<string, number>();
  for (const id of leafIds) {
    const l = leafById.get(id);
    if (!l) continue;
    const roleKey = roleKeyOf(l) ?? '_';
    const key = `${l.manifestId}:${roleKey}`;
    tally.set(key, (tally.get(key) ?? 0) + 1);
  }
  return [...tally.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|');
}

const TIER_ORDER: Record<InfraTier, number> = {
  ingress: 0,
  network: 1,
  compute: 2,
  data: 3,
  shared: 4,
  other: 5,
};

const SUBNET_TIER_ORDER: Record<SubnetTier, number> = {
  public: 0,
  private: 1,
  data: 2,
};

// ─── Constants ───────────────────────────────────────────────────

const V = {
  vpcPadding: 32,
  vpcHeaderH: 40,
  vpcGap: 60,

  ingressRowGap: 24,
  /** Horizontal spacing between subnet frames within the same tier row. */
  subnetGridGap: 32,
  /**
   * Vertical spacing between tier rows (public → private → data).
   * Must be >= `groupPad*2 + groupHeader + ~12` so consecutive
   * tier-group frames don't overlap — previously reused
   * `subnetGridGap` here and the next tier-group's title bar collided
   * with the previous tier-group's bottom edge.
   */
  tierRowGap: 60,

  subnetPadding: 20,
  subnetHeaderH: 32,
  subnetMinW: 260,
  subnetMinH: 140,

  leafGap: 16,
  leafRowGap: 20,
  subnetCols: 3,

  sharedLaneGap: 80,
  sharedLaneHeaderH: 36,

  clusterMinMembers: 3,
  clusterExtraH: 18,  // room for count badge

  fanInThreshold: 4,
};

// ─── Engine ──────────────────────────────────────────────────────

export class InfraLayoutEngine {
  layout(input: InfraLayoutInput): InfraLayoutOutput {
    const { leaves, containers, containmentEdges, flowEdges } = input;

    // ── Indices ──────────────────────────────────────────────────
    const leafById = new Map(leaves.map((l) => [l.id, l]));
    const containerById = new Map(containers.map((c) => [c.id, c]));

    // First-parent-wins containment tree (children can only have one parent
    // in our layout; the canvas-side edge list still carries extras).
    const parentOf = new Map<string, string>();
    for (const e of containmentEdges) {
      if (!parentOf.has(e.target)) parentOf.set(e.target, e.source);
    }

    // Walk up to find ancestors of a given resource type.
    const findAncestor = (startId: string, type: string): string | null => {
      let cur = parentOf.get(startId);
      let hops = 0;
      while (cur && hops++ < 16) {
        const c = containerById.get(cur);
        if (c && c.resourceType === type) return cur;
        cur = parentOf.get(cur);
      }
      return null;
    };

    // ── Fan-in detection (for "hoist shared ingress" behavior) ───
    const incomingCount = new Map<string, Set<string>>();
    for (const e of flowEdges) {
      if (!incomingCount.has(e.target)) incomingCount.set(e.target, new Set());
      incomingCount.get(e.target)!.add(e.source);
    }
    const isFanInHub = (id: string): boolean =>
      (incomingCount.get(id)?.size ?? 0) >= V.fanInThreshold;

    // ── Bucket leaves by VPC/subnet ──────────────────────────────
    // Scheme per leaf:
    //   - Tier = ingress OR fan-in → VPC ingress row (or global ingress if no VPC)
    //   - Tier = shared OR no VPC ancestor → global shared lane
    //   - else → subnet bucket (subnet id = direct subnet parent if any;
    //            otherwise VPC-level "floor" bucket)
    const vpcIds = new Set<string>();
    const subnetOfLeaf = new Map<string, string | null>(); // null if VPC-level
    const vpcOfLeaf = new Map<string, string | null>();
    const leafTier = new Map<string, InfraTier>();
    const ingressLeaves = new Map<string, string[]>();    // vpcId → leafIds
    const subnetLeaves = new Map<string, string[]>();     // subnetId → leafIds
    const vpcFloorLeaves = new Map<string, string[]>();   // vpcId → leafIds w/o subnet
    const sharedLeaves: string[] = [];                    // no VPC OR tier=shared
    const globalIngress: string[] = [];                   // ingress w/o VPC

    for (const leaf of leaves) {
      const tier = classifyTier(leaf.manifestId);
      leafTier.set(leaf.id, tier);

      const vpcId = findAncestor(leaf.id, 'vpc');
      const subnetId = findAncestor(leaf.id, 'subnet');
      vpcOfLeaf.set(leaf.id, vpcId);
      subnetOfLeaf.set(leaf.id, subnetId);
      if (vpcId) vpcIds.add(vpcId);

      // Shared tier always goes to shared lane even if it has a VPC parent —
      // things like KMS/IAM tagged to a VPC are still "pulled out" visually.
      if (tier === 'shared') {
        sharedLeaves.push(leaf.id);
        continue;
      }

      if (!vpcId) {
        // No VPC ancestor → shared lane. Ingress w/o VPC still stays at top.
        if (tier === 'ingress' || isFanInHub(leaf.id)) globalIngress.push(leaf.id);
        else sharedLeaves.push(leaf.id);
        continue;
      }

      // Hoist ingress + fan-in hubs into the VPC's ingress row.
      if (tier === 'ingress' || isFanInHub(leaf.id)) {
        const arr = ingressLeaves.get(vpcId) ?? [];
        arr.push(leaf.id);
        ingressLeaves.set(vpcId, arr);
        continue;
      }

      if (subnetId) {
        const arr = subnetLeaves.get(subnetId) ?? [];
        arr.push(leaf.id);
        subnetLeaves.set(subnetId, arr);
      } else {
        const arr = vpcFloorLeaves.get(vpcId) ?? [];
        arr.push(leaf.id);
        vpcFloorLeaves.set(vpcId, arr);
      }
    }

    // ── Subnet tier classification (public/private/data) ─────────
    const subnetTier = new Map<string, SubnetTier>();
    for (const c of containers) {
      if (c.resourceType !== 'subnet') continue;
      subnetTier.set(c.id, this.classifySubnet(c, subnetLeaves.get(c.id) ?? [], leafById));
    }

    // ── Cluster + lay out per subnet / lane ──────────────────────
    const clusters = new Map<string, InfraCluster>();
    const hiddenMemberIds = new Set<string>();
    const edgeRewrites = new Map<string, string>();
    const positions = new Map<string, { x: number; y: number }>();
    const frames = new Map<string, InfraFrame>();
    const frameShapes = new Map<string, string[]>();

    // Helper: cluster a group of leaves living in the same bucket.
    // Returns ordered "placement units" — each is either a leaf (single
    // shape) or a cluster (one shape standing for N members).
    type Unit =
      | { kind: 'leaf'; id: string; width: number; height: number; tier: InfraTier; manifestId: string }
      | { kind: 'cluster'; cluster: InfraCluster };

    const clusterBucket = (
      leafIds: string[],
      parentContainerId: string | null,
    ): Unit[] => {
      // Group by {manifestId, roleKey}. Role-key derivation: see roleKeyOf().
      // Null roleKey falls back to manifest-only grouping so legacy
      // untagged resources still cluster.
      const groups = new Map<string, { manifestId: string; roleKey: string | null; memberIds: string[] }>();
      for (const id of leafIds) {
        const l = leafById.get(id);
        if (!l) continue;
        const roleKey = roleKeyOf(l);
        const groupKey = `${l.manifestId}::${roleKey ?? ''}`;
        const existing = groups.get(groupKey);
        if (existing) {
          existing.memberIds.push(id);
        } else {
          groups.set(groupKey, { manifestId: l.manifestId, roleKey, memberIds: [id] });
        }
      }

      const units: Unit[] = [];
      for (const group of groups.values()) {
        const { manifestId, roleKey, memberIds } = group;
        const first = leafById.get(memberIds[0])!;
        const tier = leafTier.get(memberIds[0]) ?? 'other';
        if (memberIds.length >= V.clusterMinMembers) {
          const repId = memberIds[0];
          const roleLabel = roleKey ? humanizeRoleKey(roleKey) : null;
          const cluster: InfraCluster = {
            id: `cluster:${parentContainerId ?? 'root'}:${manifestId}:${roleKey ?? '_'}`,
            repId,
            manifestId,
            resourceType: first.resourceType,
            memberIds,
            count: memberIds.length,
            parentContainerId,
            tier,
            width: first.width,
            height: first.height + V.clusterExtraH,
            label: roleLabel
              ? `${roleLabel} ×${memberIds.length}`
              : `${first.resourceType} ×${memberIds.length}`,
            roleLabel,
            roleKey,
          };
          clusters.set(repId, cluster);
          for (const m of memberIds) {
            if (m !== repId) hiddenMemberIds.add(m);
            edgeRewrites.set(m, repId);
          }
          units.push({ kind: 'cluster', cluster });
        } else {
          for (const id of memberIds) {
            const l = leafById.get(id)!;
            units.push({
              kind: 'leaf',
              id,
              width: l.width,
              height: l.height,
              tier: leafTier.get(id) ?? 'other',
              manifestId,
            });
          }
        }
      }

      // Sort units: tier first, then manifestId alphabetical, then size.
      units.sort((a, b) => {
        const ta = a.kind === 'leaf' ? a.tier : a.cluster.tier;
        const tb = b.kind === 'leaf' ? b.tier : b.cluster.tier;
        if (TIER_ORDER[ta] !== TIER_ORDER[tb]) return TIER_ORDER[ta] - TIER_ORDER[tb];
        const ma = a.kind === 'leaf' ? a.manifestId : a.cluster.manifestId;
        const mb = b.kind === 'leaf' ? b.manifestId : b.cluster.manifestId;
        return ma.localeCompare(mb);
      });
      return units;
    };

    // Lay a list of units into a fixed-column grid. Returns the
    // rendered bounding box (width/height) and records each unit's
    // local coord inside the bucket.
    const layoutUnitsGrid = (
      units: Unit[],
      localPositions: Map<string, { x: number; y: number }>,
      cols: number = V.subnetCols,
    ): { width: number; height: number } => {
      if (units.length === 0) return { width: 0, height: 0 };

      // All units share a row height = max height in that row. To keep the
      // grid clean we take the max across the whole bucket (infra shapes
      // are usually similar size anyway).
      const rowH = units.reduce((m, u) => {
        const h = u.kind === 'leaf' ? u.height : u.cluster.height;
        return Math.max(m, h);
      }, 0);
      const colW = units.reduce((m, u) => {
        const w = u.kind === 'leaf' ? u.width : u.cluster.width;
        return Math.max(m, w);
      }, 0);

      let maxX = 0, maxY = 0;
      units.forEach((u, i) => {
        const r = Math.floor(i / cols);
        const c = i % cols;
        const x = c * (colW + V.leafGap);
        const y = r * (rowH + V.leafRowGap);
        const id = u.kind === 'leaf' ? u.id : u.cluster.repId;
        localPositions.set(id, { x, y });
        maxX = Math.max(maxX, x + colW);
        maxY = Math.max(maxY, y + rowH);
      });
      return { width: maxX, height: maxY };
    };

    // ── Walk VPCs ────────────────────────────────────────────────
    // Each VPC frame is laid out independently then stacked top-to-bottom.
    // Left margin stays 0 for the engine; materializer adds its own origin.
    let cursorY = 0;
    const vpcIdList = [...vpcIds].sort((a, b) => {
      const na = containerById.get(a)?.name ?? a;
      const nb = containerById.get(b)?.name ?? b;
      return na.localeCompare(nb);
    });

    // Precompute subnet child layouts (for width estimate of VPC).
    type SubnetBlock = {
      subnetId: string;
      tier: SubnetTier;
      innerBounds: { width: number; height: number };
      frameW: number;
      frameH: number;
      shapeIds: string[];
      localPositions: Map<string, { x: number; y: number }>;
    };

    const computeSubnetBlock = (subnetId: string): SubnetBlock => {
      const units = clusterBucket(subnetLeaves.get(subnetId) ?? [], subnetId);
      const local = new Map<string, { x: number; y: number }>();
      const inner = layoutUnitsGrid(units, local, V.subnetCols);
      const frameW = Math.max(inner.width + V.subnetPadding * 2, V.subnetMinW);
      const frameH = Math.max(
        inner.height + V.subnetPadding * 2 + V.subnetHeaderH,
        V.subnetMinH,
      );
      return {
        subnetId,
        tier: subnetTier.get(subnetId) ?? 'private',
        innerBounds: inner,
        frameW,
        frameH,
        shapeIds: units.map((u) => (u.kind === 'leaf' ? u.id : u.cluster.repId)),
        localPositions: local,
      };
    };

    for (const vpcId of vpcIdList) {
      const vpcContainer = containerById.get(vpcId);
      if (!vpcContainer) continue;

      // Ingress row for this VPC.
      const ingressUnits = clusterBucket(ingressLeaves.get(vpcId) ?? [], vpcId);
      const ingressLocal = new Map<string, { x: number; y: number }>();
      const ingressInner = layoutUnitsGrid(ingressUnits, ingressLocal, 999); // all in one row

      // VPC-floor leaves (have VPC but no subnet).
      const floorUnits = clusterBucket(vpcFloorLeaves.get(vpcId) ?? [], vpcId);
      const floorLocal = new Map<string, { x: number; y: number }>();
      const floorInner = layoutUnitsGrid(floorUnits, floorLocal, V.subnetCols);

      // Subnet blocks, sorted into rows by subnet tier.
      const subnetsInVpc = containers.filter(
        (c) => c.resourceType === 'subnet' && parentOf.get(c.id) === vpcId,
      );
      // Include subnets reachable via region or nested containers.
      for (const c of containers) {
        if (c.resourceType !== 'subnet') continue;
        if (subnetsInVpc.some((s) => s.id === c.id)) continue;
        if (findAncestor(c.id, 'vpc') === vpcId) subnetsInVpc.push(c);
      }
      const subnetBlocks = subnetsInVpc.map((s) => computeSubnetBlock(s.id));
      // Only keep subnet blocks that have at least one shape OR have a
      // name worth showing.
      const subnetBlocksKept = subnetBlocks.filter(
        (b) => b.shapeIds.length > 0 || subnetLeaves.get(b.subnetId)?.length,
      );

      subnetBlocksKept.sort((a, b) => {
        if (SUBNET_TIER_ORDER[a.tier] !== SUBNET_TIER_ORDER[b.tier]) {
          return SUBNET_TIER_ORDER[a.tier] - SUBNET_TIER_ORDER[b.tier];
        }
        const na = containerById.get(a.subnetId)?.name ?? a.subnetId;
        const nb = containerById.get(b.subnetId)?.name ?? b.subnetId;
        return na.localeCompare(nb);
      });

      // Arrange subnet blocks: each subnet-tier becomes a horizontal row.
      const rowsByTier = new Map<SubnetTier, SubnetBlock[]>();
      for (const b of subnetBlocksKept) {
        const arr = rowsByTier.get(b.tier) ?? [];
        arr.push(b);
        rowsByTier.set(b.tier, arr);
      }

      // ── Place shapes absolutely inside this VPC ──────────────
      const vpcInnerX0 = V.vpcPadding;
      const vpcInnerY0 = V.vpcHeaderH;
      let innerY = vpcInnerY0;

      // Row 1: ingress (full-width horizontal strip). Positions written
      // in VPC-local coords; shiftVpcPositions moves them into absolute
      // space at the end of the VPC loop.
      if (ingressUnits.length > 0) {
        for (const [id, p] of ingressLocal) {
          positions.set(id, {
            x: vpcInnerX0 + p.x,
            y: innerY + p.y,
          });
        }
        innerY += ingressInner.height + V.ingressRowGap;
      }

      // Row 2+: subnet-tier rows (public → private → data).
      const tierOrderKeys: SubnetTier[] = ['public', 'private', 'data'];
      const rowHeights: number[] = [];
      let vpcContentMaxX = ingressInner.width;
      // Track synthetic tier-group frames added in this VPC so
      // shiftVpcPositions can translate them into absolute coords
      // alongside the subnet frames they wrap.
      const tierGroupIdsInVpc: string[] = [];
      const groupPad = 12;
      const groupHeader = 22;

      let drawnTierRows = 0;
      let lastRowHadTierGroup = false;
      for (const tier of tierOrderKeys) {
        const row = rowsByTier.get(tier);
        if (!row || row.length === 0) continue;
        // Inter-row gap: fires BEFORE each non-first row, never after
        // the last one. The previous pattern added `tierRowGap` after
        // every row — including the terminal one — so a full gap of
        // dead space hung below the last tier-group inside the VPC.
        if (drawnTierRows > 0) innerY += V.tierRowGap;
        drawnTierRows += 1;

        const rowH = row.reduce((m, b) => Math.max(m, b.frameH), 0);
        let rowX = vpcInnerX0;
        const rowStartX = vpcInnerX0;
        const rowStartY = innerY;
        for (const block of row) {
          const frameX = rowX;
          const frameY = innerY;
          // Frame is absolute (will be offset by the VPC's own x/y once we
          // know it). For now we record frame coords relative to VPC origin
          // and then shift after we know vpcX.
          frames.set(block.subnetId, {
            x: frameX,
            y: frameY,
            width: block.frameW,
            height: block.frameH,
            containerId: block.subnetId,
            resourceType: 'subnet',
            tier: block.tier,
          });
          frameShapes.set(block.subnetId, block.shapeIds);

          // Child shape positions, relative to subnet frame content origin.
          for (const [id, p] of block.localPositions) {
            positions.set(id, {
              x: frameX + V.subnetPadding + p.x,
              y: frameY + V.subnetHeaderH + V.subnetPadding + p.y,
            });
          }
          rowX += block.frameW + V.subnetGridGap;
        }
        const rowEndX = rowX - V.subnetGridGap;
        rowHeights.push(rowH);

        // M2 tier-group frame: when a VPC has ≥ 2 subnets of the same
        // tier, wrap them in an outer frame. Makes the "public-a /
        // public-b / public-c" AZ pattern obvious as one logical tier.
        const hasTierGroup = row.length >= 2;
        if (hasTierGroup) {
          const groupId = `tier-group:${vpcId}:${tier}`;
          frames.set(groupId, {
            x: rowStartX - groupPad,
            y: rowStartY - groupPad - groupHeader,
            width: (rowEndX - rowStartX) + groupPad * 2,
            height: rowH + groupPad * 2 + groupHeader,
            containerId: groupId,
            resourceType: 'tier-group',
            tier,
          });
          frameShapes.set(groupId, row.map((b) => b.subnetId));
          tierGroupIdsInVpc.push(groupId);
        }

        // Track the furthest-right pixel the row draws. When a tier
        // group wraps the row, its right edge extends `groupPad` past
        // the last subnet frame — the VPC-width math must see that
        // extension or the right-most subnet bleeds outside the VPC
        // frame. Same for the left (rowStartX - groupPad), which is
        // accounted for by `vpcInnerX0` already.
        const rowRightEdge = hasTierGroup ? rowEndX + groupPad : rowEndX;
        vpcContentMaxX = Math.max(vpcContentMaxX, rowRightEdge - vpcInnerX0);

        lastRowHadTierGroup = hasTierGroup;
        innerY += rowH;
      }

      // After the last tier row, make sure the next section (floor
      // row, shared lane, VPC bottom padding) clears the tier-group
      // frame's bottom border if there was one. Without this, floor
      // leaves would abut the tier-group outline.
      if (drawnTierRows > 0) {
        innerY += (lastRowHadTierGroup ? groupPad : 0) + V.leafRowGap;
      }

      // Row last: VPC-floor leaves, as a grid below the subnets.
      if (floorUnits.length > 0) {
        for (const [id, p] of floorLocal) {
          positions.set(id, {
            x: vpcInnerX0 + p.x,
            y: innerY + p.y,
          });
        }
        innerY += floorInner.height + V.leafRowGap;
        vpcContentMaxX = Math.max(vpcContentMaxX, floorInner.width);
      }

      // VPC-local dimensions. All positions & frames added above live in
      // VPC-local coords; shift them into absolute space at (vpcX, cursorY).
      const vpcW = Math.max(
        vpcContentMaxX + V.vpcPadding * 2,
        V.subnetMinW + V.vpcPadding * 2,
      );
      const vpcH = innerY + V.vpcPadding;

      const vpcX = 0;
      shiftVpcPositions(vpcX, cursorY, positions, frames, {
        ingressIds: [...ingressLocal.keys()],
        subnetBlocks: subnetBlocksKept,
        floorIds: [...floorLocal.keys()],
        tierGroupIds: tierGroupIdsInVpc,
      });

      // Store VPC frame.
      frames.set(vpcId, {
        x: vpcX,
        y: cursorY,
        width: vpcW,
        height: vpcH,
        containerId: vpcId,
        resourceType: 'vpc',
      });
      const vpcChildShapes: string[] = [
        ...ingressLocal.keys(),
        ...floorLocal.keys(),
      ];
      // Subnet shape ownership is via subnet frames; VPC frame just owns
      // ingress + floor leaves directly.
      frameShapes.set(vpcId, vpcChildShapes);

      cursorY += vpcH + V.vpcGap;
    }

    // ── Global ingress (no VPC) ──────────────────────────────────
    let maxX = 0;
    if (globalIngress.length > 0) {
      const units = clusterBucket(globalIngress, null);
      const local = new Map<string, { x: number; y: number }>();
      const inner = layoutUnitsGrid(units, local, 999);
      for (const [id, p] of local) {
        positions.set(id, { x: p.x, y: cursorY + p.y });
      }
      cursorY += inner.height + V.sharedLaneGap;
      maxX = Math.max(maxX, inner.width);
    }

    // ── Shared services lane ─────────────────────────────────────
    if (sharedLeaves.length > 0) {
      const units = clusterBucket(sharedLeaves, null);
      const local = new Map<string, { x: number; y: number }>();
      const inner = layoutUnitsGrid(units, local, 6);
      const laneY = cursorY + V.sharedLaneHeaderH;
      for (const [id, p] of local) {
        positions.set(id, { x: p.x, y: laneY + p.y });
      }
      cursorY = laneY + inner.height + V.vpcGap;
      maxX = Math.max(maxX, inner.width);
    }

    // Final total bounds.
    let totalW = maxX;
    let totalH = cursorY;
    for (const f of frames.values()) {
      totalW = Math.max(totalW, f.x + f.width);
      totalH = Math.max(totalH, f.y + f.height);
    }

    // ── Mirror detection ─────────────────────────────────────────
    // Group subnet frames by (vpcId, tier) and compute each subnet's
    // stack signature. Mark matching subnets as mirrored so the canvas
    // can render them with a shared header.
    const subnetsByVpcTier = new Map<string, Array<{ subnetId: string; signature: string }>>();
    for (const c of containers) {
      if (c.resourceType !== 'subnet') continue;
      const vpcId = findAncestor(c.id, 'vpc') ?? '_';
      const tier = subnetTier.get(c.id) ?? 'private';
      const sig = stackSignature(subnetLeaves.get(c.id) ?? [], leafById);
      const key = `${vpcId}::${tier}`;
      const arr = subnetsByVpcTier.get(key) ?? [];
      arr.push({ subnetId: c.id, signature: sig });
      subnetsByVpcTier.set(key, arr);
      const f = frames.get(c.id);
      if (f) frames.set(c.id, { ...f, signature: sig });
    }
    for (const arr of subnetsByVpcTier.values()) {
      if (arr.length < 2) continue;
      const sigCounts = new Map<string, number>();
      for (const { signature } of arr) {
        sigCounts.set(signature, (sigCounts.get(signature) ?? 0) + 1);
      }
      for (const { subnetId, signature } of arr) {
        if ((sigCounts.get(signature) ?? 0) >= 2) {
          const f = frames.get(subnetId);
          if (f) frames.set(subnetId, { ...f, mirrored: true });
        }
      }
    }

    // ── Shared references ────────────────────────────────────────
    // For every flow edge whose target (or source, for outbound cases)
    // is a shared-tier resource, record the reference. The shared
    // resource itself still lives in the shared lane; the canvas uses
    // this map to render inline chips on the referencing resources
    // and to populate the shared-service drawer.
    const sharedReferences = new Map<string, SharedReference>();
    const sharedIds = new Set(sharedLeaves);
    const recordRef = (sharedId: string, referrerId: string): void => {
      const leaf = leafById.get(sharedId);
      if (!leaf) return;
      const existing = sharedReferences.get(sharedId);
      if (existing) {
        if (!existing.referencedBy.includes(referrerId)) {
          existing.referencedBy.push(referrerId);
        }
        return;
      }
      sharedReferences.set(sharedId, {
        resourceId: sharedId,
        manifestId: leaf.manifestId,
        resourceType: leaf.resourceType,
        label: leaf.name || leaf.manifestId,
        referencedBy: [referrerId],
      });
    };
    for (const e of flowEdges) {
      if (sharedIds.has(e.target) && !sharedIds.has(e.source)) {
        recordRef(e.target, e.source);
      } else if (sharedIds.has(e.source) && !sharedIds.has(e.target)) {
        recordRef(e.source, e.target);
      }
    }

    return {
      positions,
      clusters,
      hiddenMemberIds,
      edgeRewrites,
      frames,
      frameShapes,
      sharedReferences,
      totalBounds: { width: totalW, height: totalH },
    };
  }

  private classifySubnet(
    subnet: InfraLayoutContainer,
    leafIds: string[],
    leafById: Map<string, InfraLayoutLeaf>,
  ): SubnetTier {
    const name = subnet.name.toLowerCase();
    if (/data|db|rds|cache|redis|memcache|aurora/.test(name)) return 'data';
    if (/public|dmz|external|edge|ingress/.test(name)) return 'public';
    if (/private|internal|app|backend/.test(name)) return 'private';

    // Fall back to contents.
    let hasData = false;
    let hasIngress = false;
    for (const id of leafIds) {
      const t = classifyTier(leafById.get(id)?.manifestId ?? '');
      if (t === 'data') hasData = true;
      if (t === 'ingress') hasIngress = true;
    }
    if (hasData) return 'data';
    if (hasIngress) return 'public';
    return 'private';
  }
}

// After a VPC block is laid out in VPC-local coords, shift every
// position/frame that lives inside it into absolute coords at
// (vpcX, vpcY).
function shiftVpcPositions(
  vpcX: number,
  vpcY: number,
  positions: Map<string, { x: number; y: number }>,
  frames: Map<string, InfraFrame>,
  groups: {
    ingressIds: string[];
    subnetBlocks: Array<{ subnetId: string; shapeIds: string[] }>;
    floorIds: string[];
    tierGroupIds?: string[];
  },
): void {
  const shift = (ids: Iterable<string>): void => {
    for (const id of ids) {
      const p = positions.get(id);
      if (!p) continue;
      positions.set(id, { x: p.x + vpcX, y: p.y + vpcY });
    }
  };

  shift(groups.ingressIds);
  shift(groups.floorIds);

  for (const b of groups.subnetBlocks) {
    const f = frames.get(b.subnetId);
    if (f) frames.set(b.subnetId, { ...f, x: f.x + vpcX, y: f.y + vpcY });
    shift(b.shapeIds);
  }

  // Tier-group frames live in VPC-local coords too; shift them into
  // absolute space so the canvas draws them around the (already-shifted)
  // subnets they wrap.
  for (const id of groups.tierGroupIds ?? []) {
    const f = frames.get(id);
    if (f) frames.set(id, { ...f, x: f.x + vpcX, y: f.y + vpcY });
  }
}
